import React, { useState } from 'react'

import { DashboardStatus, WhoCanView } from 'types/GlobalDashboardGroup'

import Alert from 'components_new/atoms/Alert'
import Box from 'components_new/atoms/Box'
import Icon from 'components_new/atoms/Icon'
import Tooltip from 'components_new/atoms/Tooltip'

import FormSection from 'components_new/molecules/FormSection'
import SelectSearch from 'components_new/molecules/SelectSearch'

import { useAnalysisTypeActions } from 'redux/actions/AnalysisTypes'
import { TAnalysisType } from 'redux/hooks/AnalysisTypes'
import { useAvailableDashboardGroupsIncludeDashboards } from 'redux/hooks/DashboardGroups'
import { useAccountSettings } from 'redux/hooks/SignedInAccount'
import { useTranslation } from 'language/translator'

interface InvestigationDashboardProps {
  analysisType: TAnalysisType
}

/**
 * The dashboard an analysis type is investigated in.
 */
const InvestigationDashboard = (props: InvestigationDashboardProps) => {
  const { analysisType } = props

  const { t } = useTranslation()
  const { tryUpdateAnalysisTypeSettings } = useAnalysisTypeActions()
  const { isAdmin } = useAccountSettings()
  const dashboardGroups = useAvailableDashboardGroupsIncludeDashboards()

  const [saving, setSaving] = useState(false)

  const { dashboard_group_id: dashboardGroupId } = analysisType

  const linkedDashboard =
    dashboardGroups.find((dashboard) => dashboard.id === dashboardGroupId) ??
    null

  // Published dashboards, plus the linked one even if it is a draft.
  const selectableDashboards = dashboardGroups.filter(
    (dashboard) =>
      dashboard.status !== DashboardStatus.DRAFT ||
      dashboard.id === dashboardGroupId
  )

  // Fallback option for a linked dashboard missing from the user's list.
  const options = [
    ...selectableDashboards.map((dashboard) => {
      const isPrivate =
        dashboard.share_settings.who_can_view === WhoCanView.ONLY_ADDED_USERS

      return {
        avatar: 'dashboard' as const,
        endAdornment: isPrivate ? (
          <Tooltip title={t('dashboards', 'only_owners_and_added_can_use')}>
            <Icon color="disabled" fontSize="small" name="Private" />
          </Tooltip>
        ) : undefined,
        id: dashboard.id,
        title: dashboard.title
      }
    }),
    ...(dashboardGroupId && !linkedDashboard
      ? [
          {
            avatar: 'dashboard' as const,
            id: dashboardGroupId,
            title: t('analysis_types', 'investigation_dashboard_no_access')
          }
        ]
      : [])
  ]
  const selected =
    options.find((option) => option.id === dashboardGroupId) ?? null

  const getAlert = () => {
    if (!linkedDashboard) return null

    if (linkedDashboard.status === DashboardStatus.DRAFT) {
      return {
        severity: 'warning' as const,
        text: t('analysis_types', 'investigation_dashboard_draft_warning')
      }
    }

    if (
      linkedDashboard.share_settings.who_can_view !== WhoCanView.ORGANIZATION
    ) {
      return {
        severity: 'info' as const,
        text: t('analysis_types', 'investigation_dashboard_restricted_info')
      }
    }

    return null
  }

  const alert = getAlert()

  const handleChange = (id: string | null) => {
    setSaving(true)

    tryUpdateAnalysisTypeSettings(
      analysisType.id,
      { dashboard_group_id: id },
      () => setSaving(false)
    )
  }

  return (
    <FormSection
      helperText={t('analysis_types', 'investigation_dashboard_description')}
      importance="optional"
      title={t('analysis_types', 'investigation_dashboard_title')}
    >
      <Tooltip
        title={
          isAdmin
            ? undefined
            : t('analysis_types', 'configured_by_admin_tooltip')
        }
      >
        <Box>
          <SelectSearch
            closeOnSelect={true}
            color="primary"
            disabled={!isAdmin || saving}
            label={t('analysis_types', 'investigation_dashboard_label')}
            multiple={false}
            noGutter={true}
            onChange={(option) =>
              handleChange(option ? (option.id as string) : null)
            }
            options={options}
            selected={selected}
            size="medium"
            sort={true}
          />
        </Box>
      </Tooltip>
      {alert ? (
        <Alert severity={alert.severity} sx={{ mt: 1 }}>
          {alert.text}
        </Alert>
      ) : null}
    </FormSection>
  )
}

export default InvestigationDashboard
